import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  BasicAccordion,
  BasicTable,
  Button,
  CardComponent,
  cellStyles4,
  NoDataVisual,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  SimpleSearch,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import PatientsTabMessages from '../../../core-utils/messages/CareNavigatorConstants';
import Loader from '../../molecules/Loader';
import { formatPatients } from '../../../core-utils/helper-utils/careNavigatorHelper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { useStores } from '../../../stores';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../atoms/Snackbar';
import AddNewMemberModal from '../AddNewMemberModal';
import MemberOfInterestModal from '../MemberOfInterestModal';
import AddCategoryModal from '../AddCategoryModal';
import {
  decryptData,
  getDateFromMonthAndYear,
} from '../../../core-utils/helper-utils/helper';
import { CARE_NAVIGATOR, PATIENT_DETAILS } from '../../../core-utils/routes';
import { useNavigate } from 'react-router-dom';
import { IEmployerGroupResponseData } from '../../../pages/HomePage';
import {
  handleHeaderMouseEnter,
  handleHeaderMouseLeave,
} from '../../../core-utils/helper-utils/tableSortHelper';

export interface PatientsTabProps {}
export interface Patient {
  _id: string;
  group_id: string;
  member_id: string;
  name: string;
  age: number;
  gender: 'Male' | 'Female' | 'Other';
  enroll_status: string;
  condition: string;
  moi: boolean;
  category: string[];
  createdAt: string;
  updatedAt: string;
  patient_created_by: string;
}
export interface Group {
  group_id: string;
  group_name: string;
  eg_nid: string;
  existing: boolean;
  totalPatients: number;
  patients: Patient[];
}

const PatientsTab: React.FC<PatientsTabProps> = ({}) => {
  const useStyles = makeStyles()({
    accordion: {
      margin: '10px 0px',
    },
    rightItem: {
      display: 'flex',
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
    },
    rightItemTitle: {
      borderRadius: '4px 0px 0px 4px',
      padding: '3px 8px',
    },
    rightItemValue: {
      background: patientAccessTheme.palette.grey[100],
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
      borderRadius: '0px 4px 4px 0px',
      padding: '3px 8px',
    },
    saveButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    emptyData: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      padding: '20px',
    },
  });

  const messages = PatientsTabMessages.CARE_NAVIGATOR_PAGE.MEMBERS_DATA;

  const getFormattedCurrentDateTime = () => {
    const now = new Date();
    const offsetDate = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000,
    );
    return offsetDate.toISOString().slice(0, 16);
  };

  const careNavigatorStore = useStores().careNavigatorStore;
  const membersStore = useStores().membersStore;
  const memberSummaryStore = useStores().memberSummaryStore;
  const employerGroupStore = useStores().employerGroupStore;
  const loginPageStore = useStores().loginPageStore;
  const tabStore = useStores().tabStore;

  const navigate = useNavigate();

  const pageSize = 10;

  const [loader] = useState<any>(<Loader />);
  const [loading, setLoading] = useState<boolean>(true);
  const [group, setGroup] = useState<Group>({
    group_id: '',
    group_name: '',
    eg_nid: '',
    existing: false,
    totalPatients: 0,
    patients: [],
  });
  const [dropDownOptions, setDropDownOptions] = useState<any[]>([]);
  const [isAddMember, setIsAddMember] = useState<boolean>(false);
  const [isAddMOI, setIsAddMOI] = useState<boolean>(false);
  const [isCategory, setIsCategory] = useState<boolean>(false);
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const [assignData, setAssignData] = useState<any>({
    group_id: '',
    eg_nid: '',
    name: '',
    age: '',
    gender: '',
    enroll_status: '',
    condition: '',
  });
  const [moiData, setMoiData] = useState<any>({
    group_id: '',
    patient_id: '',
    member_id: '-',
    patient_name: '',
    group_name: '',
    type_of_trigger: '',
    priority: '',
    selfAssign: false,
    interaction: false,
    close: false,
    case_id: '',
    cn_id: '',
    interaction_date: getFormattedCurrentDateTime(),
    type: '',
    mode: '',
    referral: '',
    details: '',
  });
  const [referrals, setReferrals] = useState<string[]>([]);
  const [categoryData, setCategoryData] = useState<any>({
    patient_id: '',
    category: [],
  });
  const [typeOfTrigger, setTypeOfTrigger] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<number>(-1);
  const [columnId, setColumnId] = useState<string>('');
  const [search, setSearch] = useState('');

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handlePagination = (event: ChangeEvent<unknown>, page: number) => {
    setPageNumber(page);
  };

  const { classes } = useStyles();

  const membersTableColumns: any[] = [
    {
      name: 'Patient Name',
      align: 'left',
      cellStyles: cellStyles4,
      width: '150px',
    },
    {
      name: 'Age',
      align: 'left',
      cellStyles: cellStyles4,
      width: '60px',
    },
    {
      name: 'Gender',
      align: 'left',
      cellStyles: cellStyles4,
      width: '80px',
    },
    {
      name: 'Condition',
      align: 'left',
      cellStyles: cellStyles4,
      width: '150px',
    },
    {
      name: 'Enroll Status',
      align: 'left',
      cellStyles: cellStyles4,
      width: '100px',
    },
    {
      name: 'M.O.I Category',
      align: 'left',
      cellStyles: cellStyles4,
      width: '150px',
      showThisColumn: true,
      sortColumnName: 'moi',
      DropDownMenuItems: dropDownOptions,
    },
    {
      name: 'Action',
      align: 'left',
      cellStyles: cellStyles4,
      width: '150px',
    },
  ];

  const formatCategories = (patients: any) => {
    const options: any[] = [];
    patients?.map((patient: any) => {
      if (patient?.category?.length > 0) {
        patient.category?.map((item: string) =>
          options.push({
            text: (
              <Typography
                lineHeight="24px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="regular"
                fontSize="12px"
              >
                {item}
              </Typography>
            ),
            actionType: item,
          }),
        );
      }
    });

    options.push({
      text: (
        <Typography
          lineHeight="24px"
          textColor={patientAccessTheme.palette.text.highemphasis}
          fontType="semibold"
          fontSize="12px"
        >
          Clear
        </Typography>
      ),
      actionType: 'Clear',
    });
    return options;
  };

  const handleAddMemberClick = (_id: string, eg_nid: string) => {
    setAssignData((prev: any) => ({
      ...prev,
      group_id: _id,
      eg_nid: eg_nid,
    }));
    setIsAddMember(true);
    return;
  };

  // const handleAddMOI = (data: any, group_name: string) => {
  //   setMoiData((prev: any) => ({
  //     ...prev,
  //     group_id: data.group_id,
  //     patient_id: data._id,
  //     member_id: data.member_id || '-',
  //     group_name: group_name,
  //     patient_name:
  //       data.patient_created_by === 'system'
  //         ? decryptData(data.name)
  //         : data.name,
  //   }));
  //   setIsAddMOI(true);
  //   return;
  // };

  const handleSave = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.patientsBaseUrl +
          properties.createPatient,
        {
          group_id: assignData.group_id,
          eg_nid: assignData.eg_nid,
          name: assignData.name,
          age: Number(assignData.age),
          gender: assignData.gender,
          enroll_status: assignData.enroll_status,
          condition: assignData.condition,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Patient added succesfully', 'success', 6000);
        }
        setIsAddMember(false);
        apiCalls();
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleAccept = async (case_id: string) => {
    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.acceptCase,
        {
          case_id: case_id,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Case Accepted succesfully', 'success', 6000);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleAddInteraction = async (case_id: string, cn_id: string) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.interactionBaseURL +
          properties.addInteraction,
        {
          patient_id: moiData.patient_id,
          group_id: moiData.group_id,
          case_id: case_id,
          cn_id: cn_id,
          interaction_date: moiData.interaction_date,
          type: moiData.type,
          mode: moiData.mode,
          referral: moiData.referral,
          details: moiData.details,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Interaction added succesfully', 'success', 6000);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const endCase = async (case_id: string, careNavData: any) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.caseSurveyBaseUrl +
          properties.createCaseSurvey,
        {
          case_id: case_id,
          cn_id: careNavData.cnId,
          cn_name: careNavData.firstName + ' ' + careNavData.lastName,
          cn_role: careNavData.role,
          patient_id: moiData.patient_id,
          group_id: moiData.group_id,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Case ended succesfully', 'success', 6000);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleAssignCase = async (case_id: string, careNavData: any) => {
    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.assignCase,
        {
          case_id: case_id,
          cn_id: careNavData.cnId,
          cn_name: careNavData.firstName + ' ' + careNavData.lastName,
          cn_role: careNavData.role,
          cn_assign_note: '',
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleAccept(case_id);
          handleOpenSnackBar(
            'Care navigator assigned & accepted succesfully',
            'success',
            6000,
          );
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleCreateCase = async () => {
    const careNavData = loginPageStore.userResponse;
    if (!careNavData) {
      handleOpenSnackBar('Care navigator details not found', 'error', 6000);
      return;
    }

    await axios
      .post(
        properties.aiqBaseUrl + properties.casesBaseUrl + properties.createCase,
        {
          patient_id: moiData.patient_id,
          member_id: moiData.member_id,
          group_name: moiData.group_name,
          type_of_trigger: moiData.type_of_trigger,
          current_health_state: {
            conditions: '',
            medications: '',
            procedures: '',
          },
          priority: moiData.priority,
          group_id: moiData.group_id,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Case added succesfully', 'success', 6000);
          if (loginPageStore.isCareNavigator && moiData.selfAssign) {
            handleAssignCase(response?.data?.data?._id, careNavData);
          }
          if (loginPageStore.isCareNavigator && moiData.interaction) {
            handleAddInteraction(
              response?.data?.data?._id,
              careNavData.cnId ?? '',
            );
          }
          if (loginPageStore.isCareNavigator && moiData.close) {
            endCase(response?.data?.data?._id, careNavData);
          }
        }
        setIsAddMOI(false);
        apiCalls();
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleCategory = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.patientsBaseUrl +
          properties.updateCategory,
        {
          patient_id: categoryData.patient_id,
          category: categoryData.category.filter(
            (name: string) => name && name !== '',
          ),
        },
      )
      .then((response) => {
        if (response.status === 200) {
          handleOpenSnackBar('Category added succesfully', 'success', 6000);
        }
        setIsCategory(false);
        apiCalls();
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const getTypeOfTriggers = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'type_of_triggers',
      },
    );
  };

  const getCategories = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'moi_categories',
      },
    );
  };

  const getPatients = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.patientsBaseUrl +
        properties.getPatients,
      {
        group_ids: [careNavigatorStore.group_id],
        sortField,
        sortOrder,
        pageNumber: pageNumber - 1,
        pageSize,
      },
    );
  };

  const getReferrals = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'referrals',
      },
    );
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.all([
      getPatients(),
      getTypeOfTriggers(),
      getCategories(),
      getReferrals(),
    ])
      .then((responses) => {
        responses.map((res: any) => {
          if (res?.status !== 200) {
            handleOpenSnackBar(res?.reason?.message, 'error', 6000);
          }
        });

        if (responses[0]?.status === 200) {
          const response = responses[0].data.data;

          const { patients, totalPatients } = response;

          patients?.sort((a: any, b: any) => {
            const nameA =
              a.patient_created_by === 'system'
                ? decryptData(a.name).toLowerCase()
                : a.name.toLowerCase();
            const nameB =
              b.patient_created_by === 'system'
                ? decryptData(b.name).toLowerCase()
                : b.name.toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setGroup(responses[0].data.data);
          setPageCount(Math.ceil(totalPatients / pageSize));
          setDropDownOptions(formatCategories(responses[0].data.data));
        }

        if (responses[1]?.status === 200) {
          setTypeOfTrigger(responses[1]?.data?.data);
        }

        if (responses[2]?.status === 200) {
          setCategories(responses[2]?.data?.data);
        }

        if (responses[3].status === 200) {
          setReferrals(responses[3]?.data?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    apiCalls();
  }, [pageNumber, sortField, sortOrder]);

  const isSaveButtonDisabled = () => {
    if (
      assignData.name === '' ||
      assignData.age === '' ||
      assignData.gender === '' ||
      assignData.enroll_status === ''
    ) {
      return true;
    }
    return false;
  };

  const isSaveButtonDisabledCase = () => {
    if (
      moiData.priority === '' ||
      moiData.type_of_trigger === '' ||
      (moiData.interaction &&
        (moiData.interaction_date === '' ||
          moiData.type === '' ||
          moiData.mode === ''))
    ) {
      return true;
    }
    return false;
  };

  const handleCategoryClick = (item: any) => {
    setCategoryData({
      category: item.category,
      patient_id: item._id,
    });
    setIsCategory(true);
  };

  const handleSort = (event: any, column: any, item: any) => {
    const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
    const sortColumnName = item.actionType;

    setSortField(sortColumnName === 'Clear' ? '' : sortColumnName);
    setSortOrder(sortOrder);
  };

  const handleMemberClick = (member_id: string, eg_nid: string) => {
    if (member_id) {
      const employerGroup = employerGroupStore.allEmployerGroupsData?.find(
        (item) => item.eg_nid === eg_nid,
      );

      if (employerGroup) {
        const employerGroupResponseData: IEmployerGroupResponseData = {
          _id: employerGroup._id,
          risk_status: employerGroup.risk_status,
          eg_nid: [employerGroup.eg_nid],
          employer_group_name: [employerGroup.employer_group_name],
          status: employerGroup.status,
          plan_start_month: employerGroup.plan_start_month,
          plan_end_month: employerGroup.plan_end_month,
          recent_plan_year: employerGroup.recent_plan_year,
          start_month: employerGroup.start_month,
          start_year: employerGroup.start_year,
          end_month: employerGroup.end_month,
          end_year: employerGroup.end_year,
          total_enrolles: employerGroup.total_enrolles,
          pmpm: employerGroup.pmpm,
          total_cost: employerGroup.total_cost,
          organization: employerGroup.organization,
          assigned_to: employerGroup.assigned_to,
          __v: employerGroup.__v,
          last_updated: employerGroup.last_updated,
          sic_code: employerGroup.sic_code,
          industry_type: employerGroup.industry_type,
          state: employerGroup.state,
          admin_and_premium_cost: employerGroup.admin_and_premium_cost,
          max_claim_date: employerGroup.max_claim_date,
          min_claim_date: employerGroup.min_claim_date,
          max_incurred_date: employerGroup.max_incurred_date,
          min_incurred_date: employerGroup.min_incurred_date,
          care_navigation_id: employerGroup.care_navigation_id,
          network_indicator_list: employerGroup.network_indicator_list,
          is_claims_present: employerGroup.is_claims_present,
        };

        employerGroupStore.setEmployerGroupResponseData(
          employerGroupResponseData,
        );
        const startDate = getDateFromMonthAndYear(
          employerGroup.start_month,
          employerGroup.recent_plan_year,
        );
        const endDate = getDateFromMonthAndYear(
          employerGroup.end_month,
          employerGroup.end_year,
          true,
        );

        membersStore.addStartDate(startDate);
        membersStore.addEndDate(endDate);
        employerGroupStore.setSelectedDuration(startDate, endDate);

        if (member_id) {
          memberSummaryStore.setPathDetails([
            [
              messages.breadCrumb.text,
              messages.breadCrumb.careNavigation,
              {
                color: patientAccessTheme.palette.primary[500],
                fontWeight: 500,
              },
              () => {
                navigate(CARE_NAVIGATOR);
              },
            ],
          ]);

          const patient_id = group.patients.find(
            (patient) => patient.member_id === member_id,
          );

          navigate(PATIENT_DETAILS, {
            state: { memberId: member_id, patientId: patient_id },
          });
        }
      }
    }
  };

  const searchValue = search?.trim().toLowerCase();

  const filteredPatients = useMemo(() => {
    if (!group?.patients?.length) {
      return [];
    }

    const normalizedSearch = searchValue?.trim().toLowerCase();

    if (!normalizedSearch || normalizedSearch.length <= 2) {
      return group.patients;
    }

    return group.patients.filter((patient) => {
      const patientName =
        patient.patient_created_by === 'system'
          ? decryptData(patient.name)
          : patient.name;

      const safeName = patientName?.toLowerCase() || '';
      const safeMemberId = patient.member_id?.toLowerCase() || '';

      return (
        safeName.includes(normalizedSearch) ||
        safeMemberId.includes(normalizedSearch)
      );
    });
  }, [group, searchValue]);

  return (
    <Grid>
      {loading ? (
        loader
      ) : (
        <Grid style={{ display: 'flex', flexDirection: 'column', gap: '26px' }}>
          <CardComponent
            title={messages.TITLE.members}
            width="1176px"
            body={
              filteredPatients.length === 0 ? (
                <Grid className={classes.emptyData}>
                  <NoDataVisual content="No Data Available" height="200px" />
                </Grid>
              ) : (
                <Grid className={classes.accordion}>
                  <BasicAccordion
                    title={group.group_name}
                    width="1100px"
                    height="70px"
                    expandDefault
                    content={
                      <BasicTable
                        width="1050px"
                        columns={membersTableColumns}
                        rowsData={formatPatients(
                          filteredPatients,
                          handleCategoryClick,
                          group.existing,
                          (member_id: string) =>
                            handleMemberClick(member_id, group.eg_nid),
                          (tab: number) =>
                            tabStore.setPatientDetailsCurrentTabIndex(tab),
                          loginPageStore.memberIdentification,
                        )}
                        handleMenuItem={handleSort}
                        showOnHoverHeader={true}
                        showPagination={true}
                        pageCount={pageCount}
                        pageNumber={pageNumber}
                        handlePagination={handlePagination}
                        columnId={columnId}
                        handleHeaderMouseEnter={(column: any) =>
                          handleHeaderMouseEnter(column, setColumnId)
                        }
                        handleHeaderMouseLeave={() =>
                          handleHeaderMouseLeave(setColumnId)
                        }
                      />
                    }
                    rightItem={
                      <Grid
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <Grid className={classes.rightItem}>
                          <Typography
                            fontSize="14px"
                            lineHeight="22px"
                            fontType="normal"
                            textColor={
                              patientAccessTheme.palette.text.lowemphasis
                            }
                            className={classes.rightItemTitle}
                          >
                            No. of members
                          </Typography>

                          <Typography
                            fontSize="14px"
                            lineHeight="22px"
                            fontType="medium"
                            textColor={
                              patientAccessTheme.palette.text.lowemphasis
                            }
                            className={classes.rightItemValue}
                          >
                            {group.totalPatients}
                          </Typography>
                        </Grid>

                        {!group.existing && (
                          <Button
                            label="Add Member"
                            variant="contained"
                            className={classes.saveButton}
                            disabled={false}
                            onClick={() =>
                              handleAddMemberClick(group.group_id, group.eg_nid)
                            }
                          />
                        )}
                      </Grid>
                    }
                  />
                </Grid>
              )
            }
            topRightItem={
              <TextField
                placeholder={messages.SEARCH}
                height="36px"
                width="305px"
                startAdornment={
                  <SimpleSearch
                    height="20px"
                    width="20px"
                    stroke={patientAccessTheme.palette.text.lowemphasis}
                  />
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(event.target.value)
                }
                showStartAdornment
                style={{ marginRight: '30px' }}
              />
            }
          />
        </Grid>
      )}
      {isAddMember && (
        <AddNewMemberModal
          open={isAddMember}
          handleClose={() => setIsAddMember(false)}
          handleSave={handleSave}
          isSaveButtonDisabled={isSaveButtonDisabled()}
          assignCareNavState={[assignData, setAssignData]}
        />
      )}
      {isAddMOI && (
        <MemberOfInterestModal
          open={isAddMOI}
          handleClose={() => setIsAddMOI(false)}
          handleSave={handleCreateCase}
          isSaveButtonDisabled={isSaveButtonDisabledCase()}
          moiDataState={[moiData, setMoiData]}
          typeOfTriggers={typeOfTrigger}
          isCareNavigator={loginPageStore.isCareNavigator}
          referrals={referrals}
          isMOITab={false}
        />
      )}
      {isCategory && (
        <AddCategoryModal
          open={isCategory}
          handleClose={() => setIsCategory(false)}
          handleSave={handleCategory}
          isSaveButtonDisabled={Object.values(categoryData).some(
            (value) => value === '',
          )}
          categoryState={[categoryData, setCategoryData]}
          categories={categories}
        />
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </Grid>
  );
};
export default PatientsTab;
